import { findingRuleHint, findingTitle } from "@/lib/finding-title";
// Fleet findings inbox. Three views:
//   - By repo: current-open counts per product (the working default)
//   - By rule: the same CVE/rule across many products
//   - List: individual findings with bulk triage (defaults to new-since-baseline)
// Deep work stays on the scan page / repo current-findings list.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import type { PaginationState } from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BugIcon, ChevronDownIcon } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { EmptyState, PermissionState } from "@/components/ui/empty-state";
import { DataTable, type Column } from "@/components/ui/data-table";
import { PageHeader, PageShell } from "@/components/ui/page";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { MetricCard } from "@/components/ui/metric-card";
import { RowActions } from "@/components/ui/row-actions";
import { SeverityBadge } from "@/components/severity-badge";
import { severityRank } from "@/lib/severity";
import {
  useFindingsByRepo,
  useFleetPosture,
  useTopVulnerableRules,
  type AggregateRow,
  type FindingsByRepoRow,
} from "@/lib/fleet";
import type { Finding, FindingStatus, Severity } from "@/lib/types";

type RepoRow = FindingsByRepoRow;
type SeverityKey = "critical" | "high" | "medium" | "low" | "info";

// Stable empty arrays — a fresh `[]` fallback each render would invalidate the
// table's data-dependent row models on every render.
const EMPTY_REPO_ROWS: RepoRow[] = [];
const EMPTY_RULE_ROWS: AggregateRow[] = [];
const EMPTY_FINDINGS: Finding[] = [];

type View = "repo" | "rule" | "list";

// view is omitted for the default "by repo" inbox so /findings links
// elsewhere do not have to pass search params.
type Search = { scope?: "new" | "all"; severity?: string; view?: View; q?: string; page?: number; sort?: string; order?: "asc" | "desc" };

export const Route = createFileRoute("/_authed/findings/")({
  validateSearch: (s: Record<string, unknown>): Search => {
    const view: View =
      s.view === "rule" || typeof s.rule_id === "string"
        ? "rule"
        : s.view === "list"
          ? "list"
          : "repo";
    const q =
      typeof s.q === "string"
        ? s.q
        : typeof s.rule_id === "string"
          ? s.rule_id
          : undefined;
    return {
      ...(view !== "repo" ? { view } : {}),
      ...(q ? { q } : {}),
      scope: s.scope === "all" || s.scope === "new" ? s.scope : undefined,
      // One severity, or a comma list from Home drill-downs (e.g. "critical,high").
      severity: typeof s.severity === "string" && s.severity.split(",").every((v) => ["critical", "high", "medium", "low", "info"].includes(v)) ? s.severity : undefined,
      page: Number.isSafeInteger(Number(s.page)) && Number(s.page) > 0 ? Number(s.page) : undefined,
      sort: typeof s.sort === "string" && ["severity", "title", "file", "first_seen"].includes(s.sort) ? s.sort : undefined,
      order: s.order === "asc" ? "asc" : "desc",
    };
  },
  component: FindingsPage,
});

function FindingsPage() {
  const params = Route.useSearch();
  const view: View =
    params.view === "rule" ? "rule" : params.view === "list" ? "list" : "repo";
  const q = params.q;
  const navigate = Route.useNavigate();

  return (
    <PageShell>
      <PageHeader
        title="All findings"
        description="Current open issues across all repositories you can access. Repeated scan observations are deduplicated. Open a repository to review its current findings."
        actions={
          <div className="flex items-center gap-1.5">
            {(
              [
                ["repo", "By repo"],
                ["rule", "By rule"],
                ["list", "List"],
              ] as const
            ).map(([id, label]) => (
              <Button
                key={id}
                type="button"
                size="sm"
                variant={view === id ? "default" : "outline"}
                aria-pressed={view === id}
                onClick={() =>
                  navigate({
                    search: id === "repo" ? { q } : { view: id, q },
                  })
                }
              >
                {label}
              </Button>
            ))}
          </div>
        }
      />

      <SeverityKpis />

      {view === "repo" ? <ByRepo /> : view === "rule" ? <ByRule /> : <FindingsList />}
    </PageShell>
  );
}

// Fleet-wide open counts from the same posture the Home page reads; renders
// nothing until it loads so a failure here never masks the table below.
function SeverityKpis() {
  const sev = useFleetPosture().data?.open_findings_by_severity;
  if (!sev) return null;
  const n = (k: string) => sev[k] ?? 0;
  const total = Object.values(sev).reduce((a, b) => a + b, 0);
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <MetricCard title="Open findings" value={total.toLocaleString()} subtitle="Deduplicated across repositories" />
      <MetricCard title="Critical" value={n("critical")} subtitle="Fix first" />
      <MetricCard title="High" value={n("high")} subtitle="Fix next" />
      <MetricCard title="Medium / low / info" value={n("medium") + n("low") + n("info")} subtitle="Backlog" />
    </div>
  );
}

// Repo-count inbox. Columns are the severity ladder plus the open total, so
// the heaviest products sort to the top on any single severity.
function ByRepo() {
  const q = useFindingsByRepo();
  const rows = q.data ?? EMPTY_REPO_ROWS;

  const columns: Column<RepoRow>[] = [
    {
      key: "name",
      header: "Repo",
      sortAccessor: (r) => r.name,
      accessor: (r) => (
        <Link
          to="/repos/$repoId"
          params={{ repoId: r.repo_id }}
          className="font-medium hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {r.name}
        </Link>
      ),
    },
    sevColumn("critical", "Crit"),
    sevColumn("high", "High"),
    sevColumn("medium", "Med"),
    sevColumn("low", "Low"),
    sevColumn("info", "Info"),
    {
      key: "total",
      header: "Open",
      align: "right",
      sortAccessor: (r) => r.total,
      accessor: (r) => (
        <span className="font-medium tabular-nums">{r.total.toLocaleString()}</span>
      ),
    },
  ];

  if (q.error instanceof ApiError && q.error.status === 403) return <PermissionState description="You do not have access to these findings." />;
  if (!q.isLoading && !q.isError && rows.length === 0) {
    return (
      <EmptyState
        icon={BugIcon}
        title="No open findings"
        description="Run a scan and current-open findings land here, grouped by product."
        cta={{ label: "Go to repos", to: "/repos" }}
      />
    );
  }

  return (
    <Card><CardContent className="p-4"><DataTable
      data={rows}
      columns={columns}
      keyExtractor={(r) => r.repo_id}
      persistKey="findings-by-repo"
      density="compact"
      loading={q.isLoading}
      isError={q.isError}
      onRetry={() => void q.refetch()}
      searchPlaceholder="Filter repos..."
      emptyMessage="No repos match"
    /></CardContent></Card>
  );
}

// Severity count cell: zero reads as a dash in a muted tone so the eye lands on
// the columns that actually have volume.
function sevColumn(key: SeverityKey, header: string): Column<RepoRow> {
  return {
    key,
    header,
    align: "right",
    sortAccessor: (r) => r[key],
    accessor: (r) => {
      const n = r[key];
      return (
        <span
          className={
            n === 0
              ? "text-muted-foreground/40 tabular-nums"
              : key === "critical"
                ? "text-status-error tabular-nums"
                : key === "high"
                  ? "text-status-warning tabular-nums"
                  : "tabular-nums"
          }
        >
          {n || "—"}
        </span>
      );
    },
  };
}

// Same rule or CVE across products, sorted by blast radius. The affected-repo
// list expands inline rather than opening a detail page.
function ByRule() {
  const q = useTopVulnerableRules(100);
  // ?q= (or ?rule_id=) narrows to matching rules, e.g. a Home chart drill-down.
  const term = Route.useSearch().q?.toLowerCase();
  const rows = useMemo(() => {
    const all = q.data ?? EMPTY_RULE_ROWS;
    return term ? all.filter((r) => `${r.key} ${r.title ?? ""}`.toLowerCase().includes(term)) : all;
  }, [q.data, term]);
  const [open, setOpen] = useState<string | null>(null);

  const columns: Column<AggregateRow>[] = [
    {
      key: "key",
      header: "Rule",
      sortAccessor: (r) => r.key,
      accessor: (r) => (
        <div className="flex items-start gap-2">
          <ChevronDownIcon
            className={`size-3.5 mt-0.5 shrink-0 text-muted-foreground transition-transform ${
              open === r.key ? "" : "-rotate-90"
            }`}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{findingTitle({ title: r.title, rule_id: r.key })}</div>
            <div className="mt-1 truncate font-mono text-xs text-muted-foreground">{r.key}</div>
            {open === r.key ? (
              <div className="mt-1.5 flex flex-wrap gap-2">
                {(r.repo_ids ?? []).map((id, i) => (
                  <Link
                    key={id}
                    to="/repos/$repoId"
                    params={{ repoId: id }}
                    className="text-xs font-medium hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {r.repo_names?.[i] || "Open repository"}
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      key: "tool",
      header: "Tool",
      sortAccessor: (r) => r.tool ?? "",
      filter: { label: "Tool" },
      accessor: (r) => (
        <span className="font-mono text-xs text-muted-foreground">{r.tool || "—"}</span>
      ),
    },
    {
      key: "severity",
      header: "Severity",
      sortAccessor: (r) => r.severity ?? "",
      filter: { label: "Severity" },
      accessor: (r) =>
        r.severity ? <SeverityBadge severity={r.severity as Severity} size="sm" /> : "—",
    },
    {
      key: "repos",
      header: "Repos",
      align: "right",
      sortAccessor: (r) => r.repos,
      accessor: (r) => <span className="tabular-nums">{r.repos}</span>,
    },
    {
      key: "findings",
      header: "Open",
      align: "right",
      sortAccessor: (r) => r.findings,
      accessor: (r) => <span className="tabular-nums">{r.findings.toLocaleString()}</span>,
    },
  ];

  if (q.error instanceof ApiError && q.error.status === 403) return <PermissionState description="You do not have access to these findings." />;
  if (!q.isLoading && !q.isError && rows.length === 0) {
    return (
      <EmptyState
        icon={BugIcon}
        title="No shared rules yet"
        description="Rules that show up in more than one product appear here first."
      />
    );
  }

  return (
    <Card><CardContent className="p-4"><DataTable
      data={rows}
      columns={columns}
      keyExtractor={(r) => r.key}
      persistKey="findings-by-rule"
      density="compact"
      loading={q.isLoading}
      isError={q.isError}
      onRetry={() => void q.refetch()}
      searchPlaceholder="Filter rules, CVEs, titles..."
      emptyMessage="No rules match"
      onRowClick={(r) => setOpen((cur) => (cur === r.key ? null : r.key))}
    /></CardContent></Card>
  );
}

function FindingsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const search = Route.useSearch();
  const scope = search.scope ?? "new";
  const setScope = (scope: "new" | "all") => void navigate({ to: "/findings", search: { ...search, view: "list", scope, page: 1 } });
  const pagination = { pageIndex: (search.page ?? 1) - 1, pageSize: 50 };
  const setPagination = (next: PaginationState) => void navigate({ to: "/findings", search: { ...search, view: "list", page: next.pageIndex + 1 } });

  const q = useQuery({
    queryKey: ["findings", "list", scope, search, pagination.pageIndex, pagination.pageSize],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(pagination.pageIndex + 1),
        per_page: String(pagination.pageSize),
        status: "open",
      });
      if (search.q) params.set("q", search.q);
      if (search.severity) params.set("severity", search.severity);
      if (search.sort) params.set("sort", search.sort === "file" ? "file_path" : search.sort);
      params.set("order", search.order ?? "desc");
      if (scope === "new") params.set("baseline_state", "new,resurfaced");
      const r = await api.get<Finding[]>(`/findings?${params.toString()}`);
      const items = r.data ?? [];
      return { items, total: r.meta?.total ?? items.length };
    },
  });
  const rows = q.data?.items ?? EMPTY_FINDINGS;
  const [pendingTriage, setPendingTriage] = useState<{ ids: string[]; status: FindingStatus } | null>(null);

  // The bulk endpoint stops at the first error without a per-item receipt.
  // Individual requests let the operator see exactly which selections failed.
  const bulk = useMutation({
    mutationFn: async (vars: { ids: string[]; status: FindingStatus }) => {
      const failed: { id: string; message: string }[] = [];
      for (const id of vars.ids) {
        try { await api.put(`/findings/${id}/status`, { status: vars.status }); }
        catch (error) { failed.push({ id, message: error instanceof Error ? error.message : "Update failed" }); }
      }
      return { updated: vars.ids.length - failed.length, failed };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["findings"] });
      qc.invalidateQueries({ queryKey: ["finding"] });
      if (result.failed.length) toast.error(`${result.updated} updated; ${result.failed.length} could not be updated`);
      else toast.success(`Updated ${result.updated} findings`);
    },
  });

  const columns: Column<Finding>[] = [
    {
      key: "severity",
      header: "Severity",
      width: "8rem",
      sortAccessor: (f) => severityRank[f.severity] ?? 0,
      accessor: (f) => <SeverityBadge severity={f.severity} size="sm" />,
    },
    {
      key: "title",
      header: "Title",
      sortAccessor: (f) => findingTitle(f),
      accessor: (f) => (
        <Link
          to="/findings/$findingId"
          params={{ findingId: f.id }}
          className="block w-44 whitespace-normal font-medium hover:underline sm:w-auto sm:min-w-56 sm:max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          {findingTitle(f)}{findingRuleHint(f) && <span className="mt-1 block max-w-44 truncate font-mono text-xs font-normal text-muted-foreground sm:max-w-sm" title={f.rule_id}>{findingRuleHint(f)}</span>}
        </Link>
      ),
    },
    {
      key: "repo",
      sortable: false,
      header: "Repo",
      sortAccessor: (f) => f.repo?.name ?? f.repo_id,
      accessor: (f) =>
        f.repo_id ? (
          <Link
            to="/repos/$repoId"
            params={{ repoId: f.repo_id }}
            className="text-xs hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {f.repo?.name || "Open repository"}
          </Link>
        ) : (
          "—"
        ),
    },
    {
      key: "file",
      header: "File",
      sortAccessor: (f) => f.file_path ?? "",
      accessor: (f) => (
        <span className="block max-w-xs truncate font-mono text-xs text-muted-foreground">
          {f.file_path || "—"}
        </span>
      ),
    },
    {
      key: "baseline",
      sortable: false,
      header: "Baseline",
      sortAccessor: (f) => f.baseline_state ?? "",
      accessor: (f) => (
        <span className="text-xs text-muted-foreground">
          {f.baseline_state || "—"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      hideHeader: true,
      sortable: false,
      align: "right",
      width: "3rem",
      accessor: (f) => (
        <RowActions label={`Actions for ${findingTitle(f)}`}>
          <DropdownMenuItem onSelect={() => void navigate({ to: "/findings/$findingId", params: { findingId: f.id } })}>Open finding</DropdownMenuItem>
          {f.repo_id && <DropdownMenuItem onSelect={() => void navigate({ to: "/repos/$repoId", params: { repoId: f.repo_id } })}>Open repository</DropdownMenuItem>}
        </RowActions>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {(
          [
            ["new", "New since baseline"],
            ["all", "All open"],
          ] as const
        ).map(([id, label]) => (
          <Button key={id} type="button" size="sm" variant={scope === id ? "default" : "outline"} aria-pressed={scope === id} onClick={() => setScope(id)}>
            {label}
          </Button>
        ))}
        <Select value={search.severity ?? "all"} onValueChange={(severity) => void navigate({ to: "/findings", search: { ...search, view: "list", severity: severity === "all" ? undefined : severity, page: 1 } })}><SelectTrigger className="h-9 w-44" aria-label="Filter by severity"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All severities</SelectItem><SelectItem value="critical,high">critical + high</SelectItem>{["critical", "high", "medium", "low", "info"].map((severity) => <SelectItem key={severity} value={severity}>{severity}</SelectItem>)}</SelectContent></Select>
      </div>
      <ConfirmDialog
        open={!!pendingTriage}
        onOpenChange={(open) => { if (!open) setPendingTriage(null); }}
        title={pendingTriage ? `Mark ${pendingTriage.ids.length} ${pendingTriage.ids.length === 1 ? "finding" : "findings"} as ${pendingTriage.status === "wont_fix" ? "won't fix" : "false positive"}?` : ""}
        description="They leave the open list and stop counting toward risk. You can reopen each one from its detail page."
        confirmLabel="Mark"
        pending={bulk.isPending}
        onConfirm={() => { if (pendingTriage) bulk.mutate(pendingTriage); setPendingTriage(null); }}
      />
      {bulk.data && <div role={bulk.data.failed.length ? "alert" : "status"} className="rounded-md border border-border p-3 text-sm"><p>{bulk.data.updated} updated{bulk.data.failed.length ? ` · ${bulk.data.failed.length} failed` : ""}</p>{bulk.data.failed.length > 0 && <><ul className="mt-2 space-y-1">{bulk.data.failed.map(({ id, message }) => <li key={id}><Link to="/findings/$findingId" params={{ findingId: id }} className="underline">{findingTitle(rows.find((f) => f.id === id) ?? { title: "Open finding" })}</Link>: {message}</li>)}</ul><button type="button" disabled={bulk.isPending} className="mt-2 underline" onClick={() => bulk.mutate({ ids: bulk.data!.failed.map((f) => f.id), status: bulk.variables!.status })}>Retry failed updates</button></>}</div>}
      {q.error instanceof ApiError && q.error.status === 403 ? <PermissionState description="You do not have access to these findings." /> : !q.isLoading && !q.isError && !search.q && !search.severity && (q.data?.total ?? 0) === 0 ? (
        scope === "new" ? (
          <EmptyState
            icon={BugIcon}
            title="No new findings since baseline"
            description="New and resurfaced issues land here. Switch to all open to triage the backlog."
            cta={{ label: "Show all open", onClick: () => setScope("all") }}
          />
        ) : (
          <EmptyState
            icon={BugIcon}
            title="No open findings"
            description="Run a scan and current-open findings land here."
            cta={{ label: "Go to repos", to: "/repos" }}
          />
        )
      ) : (
        <Card><CardContent className="p-4"><DataTable
          data={rows}
          columns={columns}
          keyExtractor={(f) => f.id}
          persistKey="findings-list"
          density="compact"
          selectable
          pageSize={pagination.pageSize}
          serverSide={{
            rowCount: q.data?.total ?? 0,
            pagination,
            onPaginationChange: setPagination,
            search: search.q ?? "",
            onSearchChange: (q) => void navigate({ to: "/findings", search: { ...search, view: "list", q: q || undefined, page: 1 }, replace: true }),
            sorting: search.sort ? [{ id: search.sort, desc: search.order !== "asc" }] : [],
            onSortingChange: ([sort]) => void navigate({ to: "/findings", search: { ...search, view: "list", sort: sort?.id, order: sort?.desc ? "desc" : "asc", page: 1 } }),
          }}
          loading={q.isLoading}
          isError={q.isError}
          onRetry={() => void q.refetch()}
          searchPlaceholder="Filter findings..."
          emptyMessage="No findings match"
          onRowClick={(f) =>
            navigate({
              to: "/findings/$findingId",
              params: { findingId: f.id },
            })
          }
          bulkActions={(selected) => (
            <>
              <Button type="button" size="sm" variant="outline" disabled={bulk.isPending} onClick={() => setPendingTriage({ ids: selected.map((f) => f.id), status: "wont_fix" })}>
                Won't fix
              </Button>
              <Button type="button" size="sm" variant="outline" disabled={bulk.isPending} onClick={() => setPendingTriage({ ids: selected.map((f) => f.id), status: "false_positive" })}>
                False positive
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => downloadFindingsJson(selected)}>
                Export JSON
              </Button>
            </>
          )}
        /></CardContent></Card>
      )}
    </div>
  );
}

function downloadFindingsJson(rows: Finding[]) {
  const blob = new Blob([JSON.stringify(rows, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "findings.json";
  a.click();
  URL.revokeObjectURL(url);
}
